import ReactMarkdown, { Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import { RenderImageInMarkdown } from "./render-image-in-markdown";
import { RenderImageSections } from "./render-image-sections";

const MarkdownRenderer = ({
  content,
  setSelectedImage,
  navigateToRoute,
}: {
  content?: string;
  title?: string;
  image?: string;
  children?: React.ReactNode;
  setSelectedImage?: (src: string) => void;
  navigateToRoute?: (route: string) => void;
}) => {
  const components: Components = {
    // Custom div handler for info-section
    div: RenderImageSections,
    // Custom image handler
    img: ({ node, src, alt, ...props }) => {
      const imageSrc = src || "";
      const onClick = () => {
        if (imageSrc && setSelectedImage) {
          setSelectedImage(imageSrc);
        }
      };

      return (
        <div
          className={setSelectedImage ? "cursor-zoom-in" : undefined}
          onClick={setSelectedImage ? onClick : undefined}
          role={setSelectedImage ? "button" : undefined}
          tabIndex={setSelectedImage ? 0 : undefined}
          onKeyDown={(event) => {
            if (!setSelectedImage || !imageSrc) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setSelectedImage(imageSrc);
            }
          }}
        >
          <RenderImageInMarkdown src={imageSrc} alt={alt || ""} isInTable={false} />
        </div>
      );
    },
    a: ({ node, children, href, ...props }) => {
      const targetHref = href || "";
      const isInternal =
        targetHref.startsWith("/") ||
        targetHref.startsWith("./") ||
        targetHref.startsWith("../");
      const isDeepLink = targetHref.startsWith("harper://");
      const deepLinkPath = isDeepLink ? targetHref.replace("harper://", "") : "";
      const hasQueryParams = deepLinkPath.includes("?");

      const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
        event.preventDefault();

        if (isDeepLink) {
          if (hasQueryParams) {
            window.location.assign(targetHref);
            return;
          }

          if (navigateToRoute) {
            navigateToRoute(deepLinkPath);
            return;
          }

          window.location.assign("/" + deepLinkPath.replace(/^\//, ""));
          return;
        }

        if (isInternal) {
          let route = targetHref;
          if (route.startsWith("./")) route = route.slice(2);
          if (route.startsWith("../")) route = route.slice(3);
          if (route.startsWith("/")) route = route.slice(1);

          if (navigateToRoute) {
            navigateToRoute(route);
          } else {
            window.location.assign("/" + route);
          }
          return;
        }

        if (targetHref) {
          window.open(targetHref, "_blank", "noopener,noreferrer");
        }
      };

      return (
        <a
          className="underline text-brown-800 hover:text-brown-900"
          href={targetHref}
          onClick={handleClick}
          {...props}
        >
          {children || targetHref}
        </a>
      );
    },
    // Style other elements
    h1: ({ node, ...props }) => (
      <h1
        className="text-3xl font-bold text-gray-900 mb-8 text-center"
        {...props}
      >
        {props.children}
      </h1>
    ),

    h2: ({ node, ...props }) => (
      <h2 className="text-2xl font-semibold text-gray-900 mb-4 mt-8" {...props}>
        {props.children}
      </h2>
    ),

    ul: ({ node, ...props }) => (
      <ul
        style={{
          listStyleType: "disc",
          paddingLeft: "1.5rem",
          marginBottom: "1rem",
        }}
        {...props}
      />
    ),

    li: ({ node, ...props }) => (
      <li
        style={{
          color: "#374151",
          lineHeight: "1.625",
          marginBottom: "0.5rem",
        }}
        {...props}
      >
        {props.children}
      </li>
    ),

    p: ({ node, ...props }) => (
      <p className="text-gray-700 leading-relaxed mb-2" {...props} />
    ),
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-cream-100 rounded-md ">
      <div className="markdown-content">
        <ReactMarkdown components={components} rehypePlugins={[rehypeRaw]}>
          {content}
        </ReactMarkdown>
        <style>
          {`
            .markdown-content a {
              color: #2563eb;
              text-decoration: underline;
              transition: color 0.2s;
            }
            .markdown-content a:hover {
              color: #1d4ed8;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default MarkdownRenderer;
